import React from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Gift,
  Users,
  Target,
  ArrowRight,
  XCircle
} from 'lucide-react';
import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

export default function BetaReferralProgram() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link href="/beta">
              <Button
                variant="default"
                size="sm"
                className="gap-1.5"
                data-testid="link-header-download"
              >
                <span className="hidden sm:inline">Join Beta Program</span>
                <span className="sm:hidden">Join Beta</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── MAIN PROMO CONTENT ── */}
      <main className="flex-1 pb-20">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto bg-card shadow-xl rounded-b-3xl overflow-hidden border-x border-b border-border">
          <div className="relative w-full bg-primary/10 py-16 px-6 flex flex-col items-center justify-center text-center border-b border-primary/20">
            <div className="bg-primary/20 p-4 rounded-full mb-6">
              <Gift className="h-12 w-12 text-primary" />
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-foreground mb-4 tracking-tight">
              Beta Referral Program
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Invite other business owners to test DailyHisaab. Help us refine the app, and earn up to <span className="text-primary font-bold">₹500 in Amazon Vouchers</span> together.
            </p>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">

          {/* How It Works Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-3xl p-6 shadow-sm border border-border flex flex-col items-center text-center">
              <Users className="h-10 w-10 text-blue-500 mb-4" />
              <h3 className="font-bold text-lg mb-2">1. Share Your Code</h3>
              <p className="text-sm text-muted-foreground">
                Find your unique Referral Code in the DailyHisaab app under <strong>Settings &gt; Refer & Earn</strong>. Share it with your network.
              </p>
            </div>
            <div className="bg-card rounded-3xl p-6 shadow-sm border border-border flex flex-col items-center text-center">
              <Target className="h-10 w-10 text-amber-500 mb-4" />
              <h3 className="font-bold text-lg mb-2">2. Complete the Challenge</h3>
              <p className="text-sm text-muted-foreground">
                Both you and your referred friends must successfully complete the mandatory <strong>14-Day Beta Challenge</strong>.
              </p>
            </div>
            <div className="bg-card rounded-3xl p-6 shadow-sm border border-border flex flex-col items-center text-center">
              <Gift className="h-10 w-10 text-green-500 mb-4" />
              <h3 className="font-bold text-lg mb-2">3. Earn Rewards</h3>
              <p className="text-sm text-muted-foreground">
                Earn ₹100 for your own completion, plus ₹100 for every successful referral (up to 4 referrals). Maximum total reward: <strong>₹500</strong>.
              </p>
            </div>
          </div>

          {/* The Golden Rule */}
          <div className="bg-amber-500/10 rounded-3xl p-8 shadow-sm border border-amber-500/20">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
              <h2 className="text-2xl font-bold text-amber-900 dark:text-amber-500">The Golden Rule</h2>
            </div>
            <p className="text-amber-800 dark:text-amber-200/80 font-medium leading-relaxed text-lg">
              To earn <strong>any</strong> referral bonuses, you MUST complete your own 14-Day Challenge. If you fail to maintain your daily testing streak, you will be disqualified from the entire program and forfeit all referral earnings, regardless of how many friends complete their tasks.
            </p>
          </div>

          {/* Reward Scenarios */}
          <div className="bg-card rounded-3xl p-8 shadow-sm border border-border">
            <h2 className="text-2xl font-bold text-foreground mb-6">Reward Scenarios</h2>
            <div className="space-y-4">

              {/* Scenario 1 */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-muted/50 rounded-2xl border border-border gap-4">
                <div>
                  <h3 className="font-bold text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Max Earnings
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    You complete the 14-day challenge AND 4 of your referrals also complete it.
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <p className="text-xs font-bold text-muted-foreground uppercase">You Earn</p>
                  <p className="text-2xl font-black text-primary">₹500</p>
                </div>
              </div>

              {/* Scenario 2 */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-muted/50 rounded-2xl border border-border gap-4">
                <div>
                  <h3 className="font-bold text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> Partial Success
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    You complete the 14-day challenge. You invited 7 people, but only 1 completes the challenge.
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <p className="text-xs font-bold text-muted-foreground uppercase">You Earn</p>
                  <p className="text-2xl font-black text-primary">₹200</p>
                </div>
              </div>

              {/* Scenario 3 */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-red-500/5 rounded-2xl border border-red-500/20 gap-4">
                <div>
                  <h3 className="font-bold text-red-700 dark:text-red-400 flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" /> Disqualified
                  </h3>
                  <p className="text-sm text-red-600/80 dark:text-red-300/80 mt-1">
                    You DO NOT complete your 14-day challenge. Even if 4 or more of your referrals succeed, you are disqualified.
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <p className="text-xs font-bold text-red-500 uppercase">You Earn</p>
                  <p className="text-2xl font-black text-red-600 dark:text-red-500">₹0</p>
                </div>
              </div>

            </div>
          </div>

          {/* Call to Action */}
          <div className="flex flex-col items-center justify-center p-8 bg-muted/30 rounded-3xl border border-border text-center">
            <h2 className="text-xl font-bold mb-3">Ready to invite your network?</h2>
            <p className="text-muted-foreground mb-6 max-w-lg">
              Make sure your referrals enter your unique code when they sign up on the Beta Registration page.
            </p>
            <Link href="/beta">
              <Button size="lg" className="h-14 px-8 text-lg font-bold rounded-xl gap-2">
                Go to Beta Registration <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-8 bg-background mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="sm" />

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with care in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                &copy;2026 DailyHisaab. All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}